import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, useIsPreview } from '../lib/auth.js';
import { routes } from '../routes.js';
import { usePWAInstall } from '../hooks/usePWAInstall.js';
import { ScopeWordmark } from '../components/ScopeWordmark.js';
import { ClientSwitcher } from '../components/portal/ClientSwitcher.js';
import { EngagementSwitcher } from '../components/portal/EngagementSwitcher.js';
import { trpc } from '../lib/trpc.js';
import { HelpDrawer } from '../components/HelpDrawer.js';
import { PortalHelpContent } from '../components/portal/PortalHelpContent.js';
import { useDisplayName } from '../hooks/useDisplayName.js';
import { NotificationOptInBanner } from '../components/NotificationOptInBanner.js';
import { PreviewBanner } from '../components/PreviewBanner.js';

/**
 * Client portal application shell. Simpler than agency: top nav (no sidebar)
 * to keep visual focus on value summary. Brand-aligned.
 *
 * Phase 4 Agent I owns this file and can extend/refine. Agent J adds routes
 * inside the <Outlet />.
 *
 * Phase 5 additions: mobile hamburger menu, install-app button.
 */

const PORTAL_NAV = [
  { label: 'Dashboard', to: routes.portalDashboard },
  { label: 'Projects', to: routes.portalProjects },
  { label: 'Approvals', to: routes.portalApprovals },
  { label: 'Reports', to: routes.portalReports },
  { label: 'Documents', to: routes.portalDocuments },
  { label: 'Invoices', to: routes.portalInvoices },
];

export function PortalShell() {
  const { auth, signOut } = useAuth();
  const isPreview = useIsPreview();
  const navigate = useNavigate();
  const { canInstall, triggerInstall } = usePWAInstall();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const unreadCountQuery = trpc.notification.unreadCount.useQuery(undefined, {
    refetchInterval: 60_000,
    enabled: auth.kind === 'contact',
  });
  const unread = unreadCountQuery.data?.count ?? 0;
  const [helpOpen, setHelpOpen] = useState(false);
  // Look up the agency's primary contact email for the "Email your agency"
  // button. Agencies have many users; we use the agency owner (the
  // SUPER_ADMIN with the earliest createdAt).
  const agencyHelpQ = trpc.agency.getForContact.useQuery(undefined, {
    enabled: auth.kind === 'contact',
  });
  const { name: contactName } = useDisplayName();

  if (auth.kind !== 'contact') return null;

  return (
    <div className="min-h-screen bg-canvas font-sans">
      <header className="border-b border-scope-border bg-white">
        <div className="mx-auto flex max-w-content items-center justify-between px-4 py-4 md:px-8">
          <div className="flex items-center gap-6 md:gap-10">
            <ScopeWordmark className="h-6 w-auto shrink-0" />

            {/* Desktop nav */}
            <nav className="hidden items-center gap-1 md:flex">
              {PORTAL_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === routes.portalDashboard}
                  className={({ isActive }) =>
                    [
                      'rounded-lg px-3 py-2 text-sm transition min-h-[44px] flex items-center',
                      isActive
                        ? 'bg-scope-cream-warm font-medium text-scope-navy'
                        : 'text-scope-text-muted hover:text-scope-navy',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications bell (desktop) */}
            <NavLink
              to={routes.portalNotifications}
              className={({ isActive }) =>
                [
                  'relative hidden h-11 w-11 items-center justify-center rounded-lg transition md:inline-flex',
                  isActive
                    ? 'bg-scope-cream-warm text-scope-navy'
                    : 'text-scope-text-muted hover:bg-scope-cream-warm hover:text-scope-navy',
                ].join(' ')
              }
              aria-label="Notifications"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unread > 0 && (
                <span className="absolute -right-0.5 -top-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-status-blue px-1.5 text-2xs font-semibold text-white">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </NavLink>

            {/* Help button (desktop) */}
            <button
              onClick={() => setHelpOpen(true)}
              aria-label="Help"
              className="hidden h-11 w-11 items-center justify-center rounded-lg text-scope-text-muted transition hover:bg-scope-cream-warm hover:text-scope-navy md:inline-flex"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </button>

            {/* Install app button */}
            {canInstall && (
              <button
                onClick={() => void triggerInstall()}
                className="hidden h-11 items-center gap-1.5 rounded-lg border border-scope-border px-3 text-xs font-medium text-scope-navy hover:bg-scope-cream-warm transition sm:inline-flex"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Install
              </button>
            )}

            {/* Desktop: engagement + client switchers + Propose project + sign out */}
            <div className="hidden md:flex md:items-center md:gap-2">
              <EngagementSwitcher />
              <ClientSwitcher />
            </div>
            {!isPreview && (
              <NavLink
                to={routes.portalProposeProject}
                className="hidden h-11 items-center rounded-lg bg-scope-green px-4 text-sm font-medium text-white transition hover:bg-scope-green/90 md:inline-flex"
              >
                Propose a project
              </NavLink>
            )}
            <button
              onClick={async () => {
                await signOut();
                navigate(routes.portalLogin);
              }}
              className="hidden text-xs text-scope-text-muted hover:text-scope-navy md:block"
            >
              {contactName} · Sign out
            </button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-scope-navy hover:bg-scope-cream-warm md:hidden"
              aria-label="Toggle navigation menu"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {mobileMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="border-t border-scope-border px-4 py-3 md:hidden">
            <nav className="flex flex-col gap-1">
              {PORTAL_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === routes.portalDashboard}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    [
                      'rounded-lg px-3 py-2.5 text-sm transition min-h-[44px] flex items-center',
                      isActive
                        ? 'bg-scope-cream-warm font-medium text-scope-navy'
                        : 'text-scope-text-muted hover:text-scope-navy hover:bg-scope-cream-warm',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to={routes.portalNotifications}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  [
                    'rounded-lg px-3 py-2.5 text-sm transition min-h-[44px] flex items-center justify-between',
                    isActive
                      ? 'bg-scope-cream-warm font-medium text-scope-navy'
                      : 'text-scope-text-muted hover:text-scope-navy hover:bg-scope-cream-warm',
                  ].join(' ')
                }
              >
                <span>Notifications</span>
                {unread > 0 && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-status-blue px-1.5 text-2xs font-semibold text-white">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </NavLink>
              <div className="mt-2 flex flex-col gap-2">
                <EngagementSwitcher />
                <ClientSwitcher />
              </div>
              {!isPreview && (
                <NavLink
                  to={routes.portalProposeProject}
                  onClick={() => setMobileMenuOpen(false)}
                  className="mt-2 flex min-h-[44px] items-center rounded-lg bg-scope-green px-4 text-sm font-medium text-white"
                >
                  Propose a project
                </NavLink>
              )}
              <button
                onClick={async () => {
                  await signOut();
                  navigate(routes.portalLogin);
                }}
                className="mt-1 flex min-h-[44px] items-center px-3 text-xs text-scope-text-muted hover:text-scope-navy"
              >
                {contactName} · Sign out
              </button>
            </nav>
          </div>
        )}
      </header>

      <PreviewBanner />

      <main className="mx-auto max-w-content px-4 py-8 md:px-8">
        <NotificationOptInBanner auth={auth} variant="light" />
        <Outlet />
      </main>

      {agencyHelpQ.data?.timezone && (
        <footer className="border-t border-scope-border-soft bg-white">
          <div className="mx-auto max-w-content px-4 py-3 text-2xs text-scope-text-faint md:px-8">
            All times shown in {agencyHelpQ.data.name}'s timezone (
            {agencyHelpQ.data.timezone.replace('_', ' ')}).
          </div>
        </footer>
      )}

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} title="Help">
        <PortalHelpContent
          agencyName={agencyHelpQ.data?.name}
          agencyContactEmail={agencyHelpQ.data?.ownerEmail ?? undefined}
        />
      </HelpDrawer>
    </div>
  );
}
